import React from 'react';
import clsx from 'clsx';
import styles from './MandateAggregatedTerms.module.scss';
import { AggregatedMandateTerms } from '../../debtProductTypes';
import { useAnimatedCounter } from '../../../../common/hooks/useAnimatedCounter';
import { mandateTermsMapping } from '../../DebtProductsConstants';
import { getTooltipContentForStrings } from '../../DebtProductsUtil';
import { truncateByCharacters } from '../../../../utils/utils';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';

interface MandateAggregatedTermsProps {
    aggregatedMandateTerms: AggregatedMandateTerms;
    className?: string;
}

const MandateAggregatedTermsComponent: React.FC<MandateAggregatedTermsProps> = ({ aggregatedMandateTerms, className }) => {
    const { maximumDealAmount, minimumRoi, maximumRoi, minimumTenure, maximumTenure, products, security } = aggregatedMandateTerms || {};

    // Label getter
    const getLabel = (key: string): string => mandateTermsMapping.find((item) => item.key === key)?.label || key;

    // Animated values
    const animatedAmount = useAnimatedCounter(maximumDealAmount, { duration: 800, decimals: 1 });
    const animatedMinRoi = useAnimatedCounter(minimumRoi, { duration: 800, decimals: 1 });
    const animatedMaxRoi = useAnimatedCounter(maximumRoi, { duration: 800, decimals: 1 });
    const animatedMinTenure = useAnimatedCounter(minimumTenure, { duration: 800, decimals: 0 });
    const animatedMaxTenure = useAnimatedCounter(maximumTenure, { duration: 800, decimals: 0 });

    /** --------------------------------
     * Formatting Utilities
     * --------------------------------*/
    const formatAmount = (value?: number) => (value != null ? `${value.toFixed(1)} Cr` : '-');

    const formatRange = (min?: number, max?: number, unit = '') => {
        if (min == null && max == null) return '-';
        if (min === max) return `${min}${unit}`;
        return `${min}${unit} - ${max}${unit}`;
    };

    const formatROI = () => formatRange(animatedMinRoi, animatedMaxRoi, '%');

    const formatTenure = () => formatRange(Math.round(animatedMinTenure), Math.round(animatedMaxTenure), 'm');

    /** --------------------------------
     * Utility for rendering list-based fields (products, security, etc.)
     * --------------------------------*/
    const renderPillList = (items: string[], pillClass: string) => {
        if (!items || items.length === 0) return <span className={styles.MandateTermValue}>-</span>;

        const first = items[0];
        const remaining = items.length - 1;

        return (
            <Tooltip
                content={getTooltipContentForStrings(items, styles)}
                direction="top"
                fitContent
                hideDelay={400}
                style={{
                    position: 'absolute',
                    bottom: '100%',
                    top: 'auto',
                    transform: 'translateX(-50%)',
                    marginBottom: '0.5rem',
                    marginLeft: '0',
                    zIndex: 99999,
                    maxWidth: '400px',
                    minWidth: '300px',
                }}
                contentCss={{
                    borderRadius: '8px',
                    position: 'relative',
                    zIndex: 99999,
                    boxShadow: '0px 0px 10px 2px #0000001A',
                    maxHeight: '400px',
                    overflowY: 'auto',
                }}
            >
                <div className={styles.MandateTermValueContainer}>
                    <div className={pillClass}>{truncateByCharacters(first, 20)}</div>
                    {remaining > 0 && <span className={styles.MoreLink}>+ {remaining} more</span>}
                </div>
            </Tooltip>
        );
    };

    return (
        <div className={clsx(styles.AggregatedMandateTerms, className)}>
            <div className={styles.MandateTermsContent}>
                {/* Amount */}
                <div className={styles.MandateTermItem}>
                    <span className={styles.MandateTermLabel}>{getLabel('maximumDealAmount')}:</span>
                    <span className={styles.MandateTermValue}>{formatAmount(animatedAmount)}</span>
                </div>

                <div className={styles.MandateTermsSeparator} />

                {/* Tenure */}
                <div className={styles.MandateTermItem}>
                    <span className={styles.MandateTermLabel}>{getLabel('tenure')}:</span>
                    <span className={styles.MandateTermValue}>{formatTenure()}</span>
                </div>

                <div className={styles.MandateTermsSeparator} />

                {/* ROI */}
                <div className={styles.MandateTermItem}>
                    <span className={styles.MandateTermLabel}>{getLabel('roi')}:</span>
                    <span className={styles.MandateTermValue}>{formatROI()}</span>
                </div>

                <div className={styles.MandateTermsSeparator} />

                {/* Products */}
                <div className={styles.MandateTermItem}>{renderPillList(products, styles.ProductPill)}</div>

                <div className={styles.MandateTermsSeparator} />

                {/* Security */}
                <div className={styles.MandateTermItem}>
                    <span className={styles.MandateTermLabel}>{getLabel('securityOffered')}:</span>
                    {renderPillList(security, styles.SecurityPill)}
                </div>
            </div>
        </div>
    );
};

export default MandateAggregatedTermsComponent;
